import { useEffect, useState } from 'react'
import { useAuth } from '@/lib/contexts/AuthContext'
import { reportAttributionFailure } from '@/lib/observability/attribution'
import { API_V1_URL } from '@/lib/api/api'
import { getTimezone } from '@/lib/api/external-api'
import { filterClausesToParams } from '@/lib/api/bridge'
import type { FilterClause } from '@/lib/routing/types'

type Counts = { trials: number | null; payments: number | null; customers?: number | null; revenue: number | null }

/** GET properties/:id/attribution-funnel. Trial and revenue fields are null when their source is unavailable. */
export interface AttributionFunnelData {
  funnel: {
    visitors: number
    trials: number | null
    payments: number | null
    customers: number | null
    revenue: number | null
    form_submissions: number | null
    /** Matched to a visit outside the filters, or on another of the account's sites */
    elsewhere: Counts
    /** Stripe records matched to no visit at all */
    not_linked: Counts
  }
  filtered: boolean
  landing_page_goals?: Record<string, { trials: number; form_submissions: number }>
}

/**
 * Visitors → trials → paying customers for the page's range and filters.
 * `utmCampaigns` narrows the funnel to those ad campaigns; null is everyone.
 */
export function useAttributionFunnel(
  propertyId: string | undefined,
  dateRange: { since: string; until: string } | null,
  filters: FilterClause[],
  utmCampaigns: string[] | null,
) {
  const { token } = useAuth()
  const [data, setData] = useState<AttributionFunnelData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const filterKey = JSON.stringify(filterClausesToParams(filters))
  const campaignKey = utmCampaigns ? utmCampaigns.join(',') : null
  const since = dateRange?.since
  const until = dateRange?.until

  useEffect(() => {
    if (!propertyId || !token || !since || !until) return
    let cancelled = false

    const load = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const params = new URLSearchParams({
          startDate: since,
          endDate: until,
          timezone: getTimezone(),
          ...JSON.parse(filterKey),
        })
        if (campaignKey != null) params.set('utm_campaigns', campaignKey)
        const res = await fetch(`${API_V1_URL}properties/${propertyId}/attribution-funnel?${params}`, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          credentials: 'include',
        })
        if (!res.ok) throw Object.assign(new Error(`HTTP ${res.status}`), { status: res.status })
        const json = await res.json()
        if (!cancelled) setData(json)
      } catch (err: any) {
        // A 400 is a filter the endpoint can't apply, not a defect
        if (err?.status !== 400) reportAttributionFailure('funnel', err, { propertyId })
        if (!cancelled) setError(err.message || 'Failed to load the funnel')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    load()
    return () => { cancelled = true }
  }, [propertyId, token, since, until, filterKey, campaignKey])

  return { data, isLoading, error }
}
